import { useState, useCallback, useRef } from "react";
import { adminInsert } from "@/lib/admin-api";
import * as XLSX from "xlsx";
import { supabase } from "@/lib/supabase";

/* ================================================================
   엑셀 파일 업로드 → DB 일괄 저장
   - .xls / .xlsx 모두 지원
   - 헤더 자동 감지 → 컬럼 매핑
   - 날짜/금액/면적 정교한 파싱
   - 미리보기 + 일괄 저장
================================================================ */

const DB_COLUMNS = [
  { key: "case_number",        label: "사건번호",      required: true },
  { key: "court",              label: "법원",          required: false },
  { key: "location",           label: "소재지",        required: true },
  { key: "property_type",      label: "종류",          required: false },
  { key: "auction_date",       label: "입찰일",        required: false },
  { key: "auction_time",       label: "입찰시간",      required: false },
  { key: "appraised_value",    label: "감정가",        required: false },
  { key: "minimum_price",      label: "최저가",        required: false },
  { key: "failure_count",      label: "유찰횟수",      required: false },
  { key: "has_tenant",         label: "임차인여부",    required: false },
  { key: "exclusive_area",     label: "전용면적",      required: false },
  { key: "land_share",         label: "대지지분",      required: false },
  { key: "floor_info",         label: "층정보",        required: false },
  { key: "building_age",       label: "건물연식",      required: false },
  { key: "owner",              label: "소유자",        required: false },
  { key: "debtor",             label: "채무자",        required: false },
  { key: "grade",              label: "등급",          required: false },
  { key: "rights_analysis",    label: "권리분석/비고", required: false },
  { key: "winning_bid",        label: "낙찰가",        required: false },
  { key: "winning_bid_ratio",  label: "낙찰가%",       required: false },
  { key: "expected_sale_price",label: "예상매각가",    required: false },
  { key: "expected_profit",    label: "예상수익",      required: false },
];

// 한국어 헤더 → DB 컬럼 자동 매핑 사전
const AUTO_MAP: [string, string][] = [
  ["사건번호", "case_number"], ["사건", "case_number"], ["경매번호", "case_number"],
  ["법원", "court"], ["담당법원", "court"], ["경매법원", "court"], ["관할법원", "court"],
  ["소재지", "location"], ["주소", "location"], ["물건소재지", "location"], ["물건주소", "location"], ["물건지", "location"],
  ["종류", "property_type"], ["물건종류", "property_type"], ["유형", "property_type"], ["물건유형", "property_type"],
  ["입찰일", "auction_date"], ["매각기일", "auction_date"], ["입찰기일", "auction_date"], ["경매일", "auction_date"],
  ["입찰시간", "auction_time"], ["시간", "auction_time"], ["매각시간", "auction_time"],
  ["감정가", "appraised_value"], ["감정평가액", "appraised_value"], ["감정평가금액", "appraised_value"], ["감정금액", "appraised_value"],
  ["최저가", "minimum_price"], ["최저입찰가", "minimum_price"], ["최저매각가격", "minimum_price"], ["최저매각가", "minimum_price"], ["입찰최저가", "minimum_price"],
  ["유찰횟수", "failure_count"], ["유찰회수", "failure_count"], ["유찰", "failure_count"], ["유찰수", "failure_count"], ["패찰횟수", "failure_count"],
  ["임차인여부", "has_tenant"], ["임차인", "has_tenant"],
  ["전용면적", "exclusive_area"], ["전용", "exclusive_area"], ["면적", "exclusive_area"],
  ["대지지분", "land_share"], ["지분", "land_share"], ["대지", "land_share"],
  ["층정보", "floor_info"], ["층수", "floor_info"], ["층", "floor_info"],
  ["건물연식", "building_age"], ["연식", "building_age"], ["준공연도", "building_age"], ["준공년도", "building_age"],
  ["소유자", "owner"],
  ["채무자", "debtor"],
  ["등급", "grade"],
  ["비고", "rights_analysis"], ["권리분석", "rights_analysis"], ["메모", "rights_analysis"], ["분석", "rights_analysis"], ["특이사항", "rights_analysis"],
  ["낙찰가", "winning_bid"], ["낙찰금액", "winning_bid"], ["실낙찰가", "winning_bid"],
  ["낙찰가%", "winning_bid_ratio"], ["낙찰율", "winning_bid_ratio"], ["낙찰비율", "winning_bid_ratio"], ["낙찰률", "winning_bid_ratio"], ["낙찰%", "winning_bid_ratio"],
  ["예상매각가", "expected_sale_price"], ["예상낙찰가", "expected_sale_price"],
  ["예상수익", "expected_profit"],
];

function autoDetect(header: string): string {
  const h = header.trim().replace(/\s+/g, "").replace(/[()（）\[\]]/g, "");
  // 정확히 일치
  for (const [key, val] of AUTO_MAP) {
    if (h === key) return val;
  }
  // 포함 관계
  for (const [key, val] of AUTO_MAP) {
    if (h.includes(key) || key.includes(h)) return val;
  }
  return "";
}

// Excel 날짜 숫자 → YYYY-MM-DD 변환
function excelDateToStr(n: number): string {
  try {
    const d = XLSX.SSF.parse_date_code(n);
    if (d && d.y && d.m && d.d) {
      return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
    }
  } catch {
    // ignore
  }
  return "";
}

// 금액 파싱 (원 단위로 통일)
// 엑셀에 만원 단위로 입력된 경우: 숫자가 너무 작으면 만원으로 판단
function parseMoney(raw: unknown): number {
  if (raw === null || raw === undefined || raw === "") return 0;
  const str = String(raw).trim();
  // 이미 원 단위 숫자: 1억 이상이면 그냥 사용
  const stripped = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(stripped);
  if (isNaN(num) || num === 0) return 0;
  // 만원 단위 감지: 값이 100000 미만이면 만원 단위로 판단 (10억 = 100000만원)
  // 단, 9자리 이상(100,000,000 = 1억)이면 원 단위
  if (num < 10000 && stripped.length < 6) {
    return Math.round(num * 10000);
  }
  return Math.round(num);
}

function parseValue(col: string, raw: unknown): unknown {
  if (raw === null || raw === undefined || raw === "") return null;
  const str = String(raw).trim();

  if (["appraised_value", "minimum_price", "winning_bid", "expected_sale_price", "expected_profit"].includes(col)) {
    return parseMoney(raw);
  }

  if (col === "failure_count" || col === "building_age") {
    const num = parseInt(str.replace(/[^0-9]/g, ""), 10);
    return isNaN(num) ? 0 : num;
  }

  if (col === "exclusive_area") {
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 0 : num;
  }

  if (col === "winning_bid_ratio") {
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? null : num;
  }

  if (col === "has_tenant") {
    if (typeof raw === "boolean") return raw;
    const s = str.toLowerCase();
    return s === "1" || s === "true" || s === "y" || s === "o" || s.includes("있") || s.includes("유");
  }

  if (col === "auction_date") {
    // 숫자형 날짜 (Excel 시리얼)
    if (typeof raw === "number" || /^\d{5}$/.test(str)) {
      const n = typeof raw === "number" ? raw : parseInt(str);
      const d = excelDateToStr(n);
      if (d) return d;
    }
    // 문자열 날짜 다양한 포맷
    // 2025.04.15 / 2025/04/15 / 2025-04-15 / 20250415
    const cleaned = str.replace(/\./g, "-").replace(/\//g, "-").trim();
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
    }
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(cleaned)) {
      const [y, m, d] = cleaned.split("-");
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return cleaned;
  }

  if (col === "grade") {
    const upper = str.toUpperCase();
    if (["A", "B", "C"].includes(upper)) return upper;
    if (str.includes("A") || str.includes("우수")) return "A";
    if (str.includes("B") || str.includes("양호")) return "B";
    return "C";
  }

  if (col === "property_type") {
    if (str === "아" || str.includes("아파트")) return "아파트";
    if (str === "다" || str.includes("다세대")) return "다세대";
    if (str === "오" || str.includes("오피스텔")) return "오피스텔";
    if (str.includes("단독")) return "단독주택";
    if (str.includes("상가") || str.includes("근린")) return "상가";
    if (str.includes("빌라")) return "빌라";
    if (str.includes("토지") || str.includes("대지")) return "토지";
    return str;
  }

  return str;
}

type MappingRow = { excelCol: string; dbCol: string };

export default function AuctionExcelUploader() {
  const [step, setStep] = useState<"upload" | "map" | "preview" | "done">("upload");
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<MappingRow[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ saved: number; skipped: number; errors: string[] } | null>(null);
  const [message, setMessage] = useState<{ type: "error" | "success" | "info"; text: string } | null>(null);
  const [debugRows, setDebugRows] = useState<Record<string, unknown>[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const parseFile = useCallback((file: File) => {
    setMessage({ type: "info", text: "파일 읽는 중..." });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        // .xls 구형 포맷도 codepage 옵션으로 한글 지원
        const wb = XLSX.read(data, {
          type: "array",
          cellDates: false,
          codepage: 949,  // 한국어 CP949
        });

        const ws = wb.Sheets[wb.SheetNames[0]];

        // 빈 행 포함 raw 배열로 읽기
        const json = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          raw: true,
        }) as unknown[][];

        // 첫 번째 행이 헤더인지 확인 (간혹 윗부분에 제목 행이 있는 경우 처리)
        let headerRowIdx = 0;
        for (let i = 0; i < Math.min(5, json.length); i++) {
          const row = json[i] as unknown[];
          const nonEmpty = row.filter((v) => v !== "" && v !== null && v !== undefined).length;
          if (nonEmpty >= 3) {
            // 숫자가 아닌 문자열이 많으면 헤더 행 판단
            const strCount = row.filter((v) => typeof v === "string" && v.trim().length > 0).length;
            if (strCount >= 3) { headerRowIdx = i; break; }
          }
        }

        const allRows = json.slice(headerRowIdx);
        if (allRows.length < 2) {
          setMessage({ type: "error", text: "데이터가 없습니다. 헤더 행 + 데이터 행이 최소 2줄 이상이어야 합니다." });
          return;
        }

        const rawHeaders = (allRows[0] as unknown[]).map((h) => String(h ?? "").trim()).filter(Boolean);
        const headerCount = rawHeaders.length;

        const dataRows = allRows.slice(1)
          .filter((r) => (r as unknown[]).slice(0, headerCount).some((v) => v !== "" && v !== null))
          .map((r) => {
            const obj: Record<string, unknown> = {};
            rawHeaders.forEach((h, i) => { obj[h] = (r as unknown[])[i] ?? ""; });
            return obj;
          });

        setFileName(file.name);
        setHeaders(rawHeaders);
        setRawRows(dataRows);
        setDebugRows(dataRows.slice(0, 3));
        const newMapping = rawHeaders.map((h) => ({ excelCol: h, dbCol: autoDetect(h) }));
        setMapping(newMapping);
        setStep("map");
        setMessage(null);
      } catch (err) {
        setMessage({
          type: "error",
          text: `파일 읽기 오류: ${err instanceof Error ? err.message : "알 수 없는 오류"} — .xlsx 또는 .xls 파일을 확인해주세요.`,
        });
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = "";
  };

  const updateMapping = (idx: number, dbCol: string) => {
    setMapping((prev) => prev.map((m, i) => i === idx ? { ...m, dbCol } : m));
  };

  const getMappedRows = () => {
    return rawRows.map((row) => {
      const mapped: Record<string, unknown> = {};
      mapping.forEach(({ excelCol, dbCol }) => {
        if (!dbCol) return;
        const raw = row[excelCol];
        if (raw === "" || raw === null || raw === undefined) return;
        const parsed = parseValue(dbCol, raw);
        if (parsed !== null && parsed !== undefined) mapped[dbCol] = parsed;
      });
      // 기본값 세팅
      if (!mapped.grade) mapped.grade = "C";
      if (!mapped.property_type) mapped.property_type = "아파트";
      if (mapped.failure_count === undefined) mapped.failure_count = 0;
      if (mapped.has_tenant === undefined) mapped.has_tenant = false;
      if (!mapped.appraised_value) mapped.appraised_value = 0;
      if (!mapped.minimum_price) mapped.minimum_price = 0;
      return mapped;
    }).filter((r) => {
      // 최소: 사건번호 or (소재지 + 입찰일) 중 하나
      return r.case_number || (r.location && r.auction_date);
    });
  };

  const mappedDbCols = [...new Set(mapping.filter((m) => m.dbCol).map((m) => m.dbCol))];
  const previewRows = getMappedRows().slice(0, 8);
  const requiredMissing = DB_COLUMNS.filter((c) => c.required && !mappedDbCols.includes(c.key));

  const saveAll = async () => {
    const rows = getMappedRows();
    if (rows.length === 0) {
      setMessage({ type: "error", text: "저장할 유효한 데이터가 없습니다. 사건번호 컬럼 매핑을 확인하세요." });
      return;
    }
    setSaving(true);
    setProgress(0);
    const errors: string[] = [];
    let saved = 0;
    let skipped = 0;
    const BATCH = 10;

    for (let i = 0; i < rows.length; i += BATCH) {
      const batch = rows.slice(i, i + BATCH);
      for (const row of batch) {
        // 중복 체크 (사건번호 기준)
        if (row.case_number) {
          const { data: existing } = await supabase
            .from("kangstudy_auction_items")
            .select("id")
            .eq("case_number", row.case_number as string)
            .maybeSingle();
          if (existing) { skipped++; continue; }
        }
        // 쓰기는 Edge Function 대행(RLS: service_role 전용). 한 건 실패가
        // 전체를 죽이지 않게 건별로 격리한다.
        try {
          await adminInsert("kangstudy_auction_items", [row]);
          saved++;
        } catch (err) {
          const msg = err instanceof Error ? err.message : "저장 실패";
          errors.push(`${row.case_number || row.location || "?"}: ${msg}`);
        }
      }
      const pct = Math.min(100, Math.round(((i + batch.length) / rows.length) * 100));
      setProgress(pct);
    }

    setResult({ saved, skipped, errors });
    setStep("done");
    setSaving(false);
  };

  const reset = () => {
    setStep("upload");
    setResult(null);
    setFileName("");
    setHeaders([]);
    setRawRows([]);
    setMapping([]);
    setDebugRows([]);
    setShowDebug(false);
    setMessage(null);
    setProgress(0);
  };

  // ── 렌더링
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-bold text-[#1C1917]">엑셀 파일 업로드</h3>
          <p className="text-xs text-[#8C8780] mt-0.5">.xlsx / .xls 파일 업로드 → 컬럼 자동 매핑 → DB 일괄 저장</p>
        </div>
        {rawRows.length > 0 && step !== "done" && (
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="text-[11px] px-2.5 py-1 rounded-md border border-[#E8E5E0] text-[#8C8780] hover:bg-[#F5F3F0] transition-all whitespace-nowrap"
          >
            <i className="ri-bug-line mr-1"></i>원본 데이터
          </button>
        )}
      </div>

      {message && (
        <div className={`px-4 py-2.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
          message.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
          message.type === "info" ? "bg-blue-50 text-blue-700 border border-blue-200" :
          "bg-red-50 text-red-700 border border-red-200"
        }`}>
          <i className={`text-sm ${message.type === "info" ? "ri-loader-4-line animate-spin" : message.type === "success" ? "ri-check-line" : "ri-alert-line"}`}></i>
          {message.text}
        </div>
      )}

      {/* 디버그: 원본 데이터 미리보기 */}
      {showDebug && debugRows.length > 0 && (
        <div className="border border-amber-200 rounded-lg bg-amber-50 p-3">
          <p className="text-[11px] font-semibold text-amber-700 mb-2">원본 데이터 (처음 3행) — 헤더 감지 확인용</p>
          <div className="overflow-x-auto">
            <table className="text-[10px] w-full min-w-max">
              <thead>
                <tr className="border-b border-amber-200">
                  {headers.map((h) => (
                    <th key={h} className="px-2 py-1 text-left text-amber-700 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {debugRows.map((row, i) => (
                  <tr key={i} className="border-b border-amber-100">
                    {headers.map((h) => (
                      <td key={h} className="px-2 py-1 text-amber-800 whitespace-nowrap max-w-[100px] truncate">
                        {String(row[h] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* STEP 1: 업로드 */}
      {step === "upload" && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
              dragging ? "border-[#C4956A] bg-[#FBF7F3]" : "border-[#D8D3CC] hover:border-[#C4956A] hover:bg-[#FAFAF8]"
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#F5F3F0] flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-excel-2-line text-2xl text-[#10B981]"></i>
            </div>
            <p className="text-sm font-semibold text-[#1C1917] mb-1">엑셀 파일을 여기로 드래그하거나 클릭해서 선택</p>
            <p className="text-xs text-[#8C8780] mb-3">.xlsx, .xls 파일 지원 · 한국어 헤더 자동 인식</p>
            <span className="inline-block px-4 py-2 text-xs font-medium bg-[#1C1917] text-white rounded-full">
              <i className="ri-upload-2-line mr-1.5"></i>파일 선택
            </span>
            <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
          </div>

          {/* 지원 컬럼 안내 */}
          <div className="border border-[#E8E5E0] rounded-lg p-4 bg-[#FAFAF8]">
            <p className="text-xs font-semibold text-[#5C5650] mb-2">자동 인식 가능한 컬럼 목록</p>
            <div className="flex flex-wrap gap-1.5">
              {DB_COLUMNS.map((c) => (
                <span key={c.key} className={`text-[11px] px-2 py-0.5 rounded-full ${
                  c.required ? "bg-[#C4956A]/15 text-[#C4956A] font-medium" : "bg-[#F0EDE8] text-[#8C8780]"
                }`}>
                  {c.label}{c.required ? " *" : ""}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-[#B8B3AE] mt-2">* 필수 컬럼 | 헤더명이 다르면 업로드 후 드롭다운으로 직접 연결 가능</p>
          </div>

          {/* 엑셀 형식 안내 */}
          <div className="border border-[#E8E5E0] rounded-lg p-4 bg-[#FAFAF8] text-xs text-[#8C8780] space-y-1">
            <p className="font-semibold text-[#5C5650]">📋 업로드 팁</p>
            <p>• 첫 번째 행이 헤더(컬럼명)여야 합니다</p>
            <p>• 금액은 원 단위 또는 만원 단위 모두 인식 (예: 450000000 또는 45000)</p>
            <p>• 날짜는 2025.04.15 / 2025-04-15 / 20250415 형식 모두 인식</p>
            <p>• 종류 컬럼: "아/다/오" 약자 또는 "아파트/다세대/오피스텔" 전체 이름 모두 인식</p>
          </div>
        </div>
      )}

      {/* STEP 2: 컬럼 매핑 */}
      {step === "map" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
            <i className="ri-file-excel-2-line text-emerald-600"></i>
            <span className="text-xs font-medium text-emerald-700">
              <span className="font-bold">{fileName}</span> — 총 <span className="font-bold">{rawRows.length}행</span> 감지됨
              <span className="text-emerald-600 ml-2">
                (자동 매핑: {mapping.filter((m) => m.dbCol).length}/{headers.length}개)
              </span>
            </span>
          </div>

          <div>
            <p className="text-xs font-semibold text-[#5C5650] mb-2">컬럼 매핑 확인 · 수동 수정</p>
            <div className="border border-[#E8E5E0] rounded-lg overflow-hidden">
              <div className="grid grid-cols-5 gap-0 bg-[#F5F3F0] border-b border-[#E8E5E0] px-4 py-2">
                <span className="text-[11px] font-semibold text-[#8C8780] col-span-2">엑셀 컬럼명</span>
                <span className="text-[11px] font-semibold text-[#8C8780] col-span-2">DB 필드</span>
                <span className="text-[11px] font-semibold text-[#8C8780] text-center">상태</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-[#F0EDE8]">
                {mapping.map((m, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 px-4 py-2 items-center hover:bg-[#FAFAF8]">
                    <div className="col-span-2">
                      <span className="text-xs font-medium text-[#1C1917]">{m.excelCol}</span>
                      {/* 원본 샘플 값 표시 */}
                      {rawRows[0]?.[m.excelCol] !== undefined && rawRows[0]?.[m.excelCol] !== "" && (
                        <p className="text-[10px] text-[#B8B3AE] truncate max-w-[120px]">
                          예: {String(rawRows[0][m.excelCol]).slice(0, 20)}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <select
                        value={m.dbCol}
                        onChange={(e) => updateMapping(idx, e.target.value)}
                        className={`w-full text-xs border rounded-md px-2 py-1 focus:outline-none focus:border-[#C4956A] ${
                          m.dbCol
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-[#E8E5E0] text-[#8C8780]"
                        }`}
                      >
                        <option value="">— 무시 —</option>
                        {DB_COLUMNS.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}{c.required ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-center">
                      {m.dbCol ? (
                        <i className="ri-check-line text-emerald-500 text-sm"></i>
                      ) : (
                        <i className="ri-close-line text-[#D8D3CC] text-sm"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {requiredMissing.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-700">
              <i className="ri-alert-line mr-1"></i>
              <span className="font-semibold">필수 컬럼 미매핑:</span> {requiredMissing.map((c) => c.label).join(", ")}
              <span className="text-amber-600 ml-1">— 위 드롭다운에서 연결해주세요</span>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={reset} className="px-4 py-2 text-xs font-medium border border-[#E8E5E0] text-[#5C5650] rounded-lg hover:bg-[#FAFAF8] transition-all whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i>다시 선택
            </button>
            <button
              onClick={() => setStep("preview")}
              disabled={requiredMissing.length > 0}
              className="px-5 py-2 text-xs font-medium bg-[#1C1917] text-white rounded-lg hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40"
            >
              미리보기 확인 <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: 미리보기 */}
      {step === "preview" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-[#5C5650]">
              미리보기 (첫 8행) — 총 변환 대상 <span className="text-[#C4956A] font-bold">{getMappedRows().length}건</span>
            </p>
            <span className="text-xs text-[#8C8780]">사건번호 중복 → 자동 제외</span>
          </div>

          <div className="border border-[#D8D3CC] rounded-lg overflow-auto max-h-72">
            <table className="text-xs w-full min-w-max">
              <thead>
                <tr className="bg-[#F5F3F0] border-b border-[#D8D3CC]">
                  {mappedDbCols.map((col) => (
                    <th key={col} className="px-3 py-2 border-r border-[#E8E5E0] font-semibold text-[#5C5650] whitespace-nowrap text-left">
                      {DB_COLUMNS.find((c) => c.key === col)?.label || col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.length === 0 ? (
                  <tr>
                    <td colSpan={mappedDbCols.length} className="px-4 py-6 text-center text-[#8C8780] text-xs">
                      변환된 데이터가 없습니다. 사건번호 또는 소재지+입찰일 컬럼 매핑을 확인하세요.
                    </td>
                  </tr>
                ) : (
                  previewRows.map((row, i) => (
                    <tr key={i} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8]">
                      {mappedDbCols.map((col) => (
                        <td key={col} className="px-3 py-1.5 border-r border-[#F0EDE8] text-[#5C5650] whitespace-nowrap max-w-[160px] truncate">
                          {col === "has_tenant" ? (row[col] ? "있음" : "없음") :
                           col === "grade" ? (
                             <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                               row[col] === "A" ? "bg-emerald-100 text-emerald-700" :
                               row[col] === "B" ? "bg-amber-100 text-amber-700" :
                               "bg-gray-100 text-gray-600"
                             }`}>{String(row[col] || "C")}</span>
                           ) :
                           ["appraised_value", "minimum_price", "winning_bid"].includes(col) && row[col]
                             ? `${Math.round((row[col] as number) / 10000).toLocaleString()}만`
                             : String(row[col] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {previewRows.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-700">
              <p className="font-semibold mb-1">데이터가 변환되지 않았습니다.</p>
              <p>• "원본 데이터" 버튼을 눌러서 헤더 감지가 올바른지 확인하세요</p>
              <p>• 사건번호 컬럼이 매핑되어 있는지 확인하세요</p>
              <p>• 매핑 수정 버튼을 눌러 컬럼을 다시 연결해보세요</p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setStep("map")} className="px-4 py-2 text-xs font-medium border border-[#E8E5E0] text-[#5C5650] rounded-lg hover:bg-[#FAFAF8] transition-all whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i>매핑 수정
            </button>
            <button
              onClick={saveAll}
              disabled={saving || previewRows.length === 0}
              className="px-6 py-2 text-xs font-semibold bg-[#C4956A] text-white rounded-lg hover:bg-[#B08050] transition-all whitespace-nowrap disabled:opacity-60"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin mr-1"></i>저장 중... {progress}%</>
              ) : (
                <><i className="ri-database-2-line mr-1"></i>{getMappedRows().length}건 DB에 저장하기</>
              )}
            </button>
          </div>

          {saving && (
            <div className="space-y-1">
              <div className="h-2 bg-[#F0EDE8] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C4956A] rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-[10px] text-[#8C8780] text-right">{progress}% 완료</p>
            </div>
          )}
        </div>
      )}

      {/* STEP 4: 완료 */}
      {step === "done" && result && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <i className="ri-check-line text-emerald-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">업로드 완료!</p>
                <p className="text-xs text-emerald-600">{fileName}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                <p className="text-2xl font-bold text-emerald-700">{result.saved}</p>
                <p className="text-[11px] text-emerald-600 mt-0.5">신규 저장</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                <p className="text-[11px] text-amber-600 mt-0.5">중복 제외</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-red-200">
                <p className="text-2xl font-bold text-red-500">{result.errors.length}</p>
                <p className="text-[11px] text-red-500 mt-0.5">오류</p>
              </div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-red-700">오류 내역 (최대 10건):</p>
              {result.errors.slice(0, 10).map((e, i) => (
                <p key={i} className="text-xs text-red-600">• {e}</p>
              ))}
              {result.errors.length > 10 && (
                <p className="text-xs text-red-400">...외 {result.errors.length - 10}건</p>
              )}
            </div>
          )}

          {result.saved > 0 && (
            <div className="bg-[#FAFAF8] border border-[#E8E5E0] rounded-lg p-3 text-xs text-[#5C5650]">
              <i className="ri-lightbulb-line text-[#C4956A] mr-1"></i>
              저장된 물건은 <span className="font-semibold">물건 관리</span> 탭에서 확인·수정하세요.
              자동 블로그는 다음 오전 7시에 새 데이터로 생성됩니다.
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={reset}
              className="px-5 py-2 text-xs font-medium bg-[#1C1917] text-white rounded-lg hover:bg-[#C4956A] transition-all whitespace-nowrap"
            >
              <i className="ri-upload-cloud-2-line mr-1"></i>새 파일 업로드
            </button>
          </div>
        </div>
      )}
    </div>
  );
}